import {
  useFeedback,
  usePermission,
  useSource,
  useTranslation,
} from "@opendash/core";
import { Source } from "@opendash/core/dist/types-generated";
import { ParseObjectForm } from "@opendash/plugin-parse";
import { Button, Modal } from "antd";
import { Dayjs } from "dayjs";
import { observer } from "mobx-react-lite";
import React from "react";
import { Async } from "../../../_shared";
import {
  Schedule_Template,
  Schedule_TemplateAttributes,
} from "../../../js/parsegenerated";
import { OpenService } from "../../../types/config";

type Props = {
  date?: Dayjs;
  onSave?: (schedule: Schedule_Template) => Async<void>;
};

export const ScheduleCreationModalButton = observer((props: Props) => {
  const t = useTranslation();
  const { handleLoadingFeedback } = useFeedback();
  const [loading, setLoading] = React.useState(false);
  const [value, setValue] =
    React.useState<Partial<Schedule_TemplateAttributes>>(undefined);
  const [source] = useSource();
  const canCreateSchedule = usePermission(
    OpenService.Permissions.Create.schedule
  );

  function setupForm() {
    setValue({
      title: "Neuer Wartungsplan am " + props.date?.format("DD.MM.YYYY"),
      cron: {
        timestamp: {
          startDate: props.date?.toDate(),
          number: 1,
          unit: "days",
        },
        schedule_type: "timestamp",
      },
    });
  }

  async function onSave() {
    setLoading(true);
    await handleLoadingFeedback(
      async () => {
        if (value) {
          const schedule = new Schedule_Template();
          schedule.set("title", value.title);
          schedule.set("description", value.description);
          schedule.set("color", value.color);
          schedule.set("notifyBeforeDue", value.notifyBeforeDue);
          schedule.set("cron", value.cron);

          const savedSchedule = await schedule.save();
          savedSchedule
            .relation("sources")
            .add(new Source({ objectId: source.id }));
          await savedSchedule.save();
          await props.onSave?.(savedSchedule);
        }
      },
      t("maintenance:schedule.create.loading"),
      t("maintenance:schedule.create.success"),
      t("maintenance:schedule.create.error")
    );
    setLoading(false);
    setValue(undefined);
  }

  if (!canCreateSchedule) return <></>;

  return (
    <>
      <Button
        type="primary"
        onClick={() => setupForm()}
        style={{ width: "100%" }}
      >
        {t("maintenance:schedule.create.button.modal")}
      </Button>
      <Modal
        open={!!value}
        onCancel={() => setValue(undefined)}
        destroyOnHidden
        footer={
          <Button type="primary" onClick={onSave} loading={loading}>
            {t("maintenance:schedule.create.button.save")}
          </Button>
        }
      >
        <ParseObjectForm
          className={Schedule_Template.className}
          fields={
            [
              "title",
              "description",
              "color",
              "notifyBeforeDue",
              "cron",
            ] as Array<keyof Schedule_TemplateAttributes>
          }
          onSave={onSave}
          setState={setValue}
          state={value}
        />
      </Modal>
    </>
  );
});
